import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material';
import { MdInfoOutline } from 'react-icons/md';
import { useGetBusinessInsights } from '@/apis/agency/businessInsights/hook';
import BusinessInsightStatCard from './BusinessInsightStatCard';
import BusinessLifecycleChart from './BusinessLifecycleChart';
import IntegrationBreakdownChart from './IntegrationBreakdownChart';
import ChurnRiskBusinessesSection from './ChurnRiskBusinessesSection';
import BusinessInsightsSkeleton, { BusinessInsightsError } from './BusinessInsightsSkeleton';
import BusinessIcon from '@/assets/icons/BusinessIcon';
import ArchivedBusinessesIcon from '@/assets/icons/ArchivedBusinessesIcon';
import InactiveBusinessIcon from '@/assets/icons/InactiveBusinessIcon';

export default function BusinessesInsights() {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetBusinessInsights();

  if (isLoading) {
    return <BusinessInsightsSkeleton />;
  }

  if (isError || !data) {
    return <BusinessInsightsError />;
  }

  const summary = data.account_status_summary;

  const statCards = [
    {
      key: 'active',
      title: 'Active',
      count: summary.active.count,
      subtitle: summary.active.on_billing != null ? `${summary.active.on_billing} on billing` : undefined,
      icon: <BusinessIcon size={18} />,
      tooltip: 'Businesses that are currently active and using the platform.'
    },
    {
      key: 'past_due',
      title: 'Past Due',
      count: summary.past_due.count,
      icon: <MdInfoOutline size={18} color={theme.palette.primary.main} />,
      tooltip:
        'Businesses with a failed payment are moved to Past Due. Their services remain active for 7 days after billing to allow time to resolve the payment.'
    },
    {
      key: 'inactive',
      title: 'Inactive',
      count: summary.inactive.count,
      icon: <InactiveBusinessIcon />,
      tooltip:
        'Businesses that have completed the Past Due period. Access disabled, and billable resources such as numbers and integrations are released. All other data and information remain available.'
    },
    {
      key: 'archived',
      title: 'Archived',
      count: summary.archived.count,
      icon: <ArchivedBusinessesIcon />,
      tooltip:
        'Businesses that have been archived & are no longer active on the platform. All data and resources are cleaned up, except billing information.'
    }
  ];

  return (
    <>
      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid key={card.key} size={{ xs: 12, sm: 6, md: 3 }}>
            <BusinessInsightStatCard
              title={card.title}
              count={card.count}
              subtitle={card.subtitle}
              icon={card.icon}
              tooltip={card.tooltip}
            />
          </Grid>
        ))}
      </Grid>
      <BusinessLifecycleChart data={data.lifecycle_trend} />
      <IntegrationBreakdownChart data={data.integration_breakdown ?? []} />
      <ChurnRiskBusinessesSection />
    </>
  );
}
